import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { t, getLocale } from '@/i18n';
import { ASSUMPTIONS, CHANNELS } from '@/data/urgencyStats';
import { VALUE_ASSUMPTIONS, pilotValue } from '@/lib/pilotValue';
import { TrendingUp } from 'lucide-react';
import { HelpTip } from '@/components/ui/help-tip';

const fmt = (n, digits = 0) => n.toLocaleString(getLocale(), { maximumFractionDigits: digits });
const fill = (text, vars) => Object.entries(vars).reduce((s, [k, v]) => s.replaceAll(`{${k}}`, typeof v === 'number' ? fmt(v, 1) : v), text);

// Number input for an assumption that the client can change in the meeting.
const Assumption = ({ label, value, onChange }) => (
  <label className="flex items-center gap-2 text-xs text-primary-foreground/80">
    {label}
    <input
      type="number" min="0" step="100" value={value}
      onChange={e => onChange(Math.max(0, Number(e.target.value) || 0))}
      className="w-24 rounded-md bg-primary-foreground text-primary px-2 py-1 text-right tabular-nums font-semibold"
    />
    {t('stats.urgency.czk')}
  </label>
);

// Value of the pilot per month (#111): total, breakdown with sources, payback against the software price.
// Shown first on the page as the main message, in navy.
export const UrgencySavings = ({ summary }) => {
  const [noShowCost, setNoShowCost] = useState(VALUE_ASSUMPTIONS.noShowCost.value);
  const [price, setPrice] = useState(VALUE_ASSUMPTIONS.softwarePrice.value);
  const v = pilotValue(summary, { noShowCost, softwarePrice: price });
  const czk = (n) => `${fmt(n)} ${t('stats.urgency.czk')}`;
  const prices = CHANNELS.map(c => `${t(`arrivals.channel.${c}`)} ${fmt(ASSUMPTIONS.priceCzk[c], 2)} ${t('stats.urgency.czk')}`).join(', ');

  return (
    <Card className="p-5 space-y-4 bg-primary text-primary-foreground border-primary">
      <div className="text-sm font-semibold text-primary-foreground/80 flex items-center gap-1">{t('stats.value.title')} <HelpTip id="value" className="text-primary-foreground/80 hover:text-primary-foreground" /></div>
      <div className="flex flex-wrap items-end gap-x-6 gap-y-2">
        <div className="text-4xl font-bold tabular-nums">
          {czk(v.total)}<span className="text-base font-medium text-primary-foreground/75"> / {t('stats.value.month')}</span>
        </div>
        {v.roi != null && (
          <div className="flex items-center gap-2 rounded-full bg-accent text-primary px-3 py-1 text-sm font-semibold">
            <TrendingUp className="h-4 w-4" /> {fill(t('stats.value.roi'), { roi: Math.round(v.roi), price: czk(price) })}
          </div>
        )}
      </div>

      <ul className="divide-y divide-primary-foreground/15">
        {v.items.map(i => (
          <li key={i.key} className="py-2 flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{t(`stats.value.${i.key}`)}</div>
              <div className="text-xs text-primary-foreground/75">
                {fill(t(`stats.value.${i.key}Calc`), i.vars)} · <span className="italic">{t(`stats.value.source.${i.source}`)}</span>
              </div>
            </div>
            <div className="text-sm font-semibold tabular-nums whitespace-nowrap">{i.value >= 0 ? '+' : '−'} {czk(Math.abs(i.value))}</div>
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap gap-x-6 gap-y-2">
        <Assumption label={t('stats.value.noShowCostInput')} value={noShowCost} onChange={setNoShowCost} />
        <Assumption label={t('stats.value.priceInput')} value={price} onChange={setPrice} />
      </div>

      <div className="text-[11px] text-primary-foreground/75 border-t border-primary-foreground/20 pt-2">
        {t('stats.urgency.assumption')
          .replace('{minutes}', ASSUMPTIONS.plannerMinutes)
          .replace('{days}', ASSUMPTIONS.workDaysPerMonth)
          .replace('{prices}', prices)} {t('stats.value.note')}
      </div>
    </Card>
  );
};
